/**
 * Branches list page
 */
import { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useBranch } from '@/contexts/BranchContext';
import { useFranchise } from '@/contexts/FranchiseContext';
import { useAuth } from '@/contexts/AuthContext';
import { BranchCard } from '@/components/branches/BranchCard';
import { BranchListView } from '@/components/branches/BranchListView';
import { LoadingState } from '@/components/shared/LoadingState';
import { EmptyState } from '@/components/shared/EmptyState';
import { ConfirmDialog } from '@/components/shared/ConfirmDialog';
import { SearchBar } from '@/components/shared/SearchBar';
import { FilterBar, FilterConfig } from '@/components/shared/FilterBar';
import { Pagination } from '@/components/shared/Pagination';
import { Breadcrumbs } from '@/components/shared/Breadcrumbs';
import { SortSelector, SortOption } from '@/components/shared/SortSelector';
import { ViewToggle, ViewMode } from '@/components/shared/ViewToggle';
import { ExportButton } from '@/components/shared/ExportButton';
import { Button } from '@/components/ui/Button';
import { useTranslation } from 'react-i18next';
import { toast } from '@/lib/toast';

const ITEMS_PER_PAGE = 9;

export default function BranchesPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { customClaims, user } = useAuth();
  const { branches, loading, deleteBranch, refreshBranches } = useBranch();
  const { franchises } = useFranchise();
  const [searchQuery, setSearchQuery] = useState('');
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [activeFilters, setActiveFilters] = useState<Record<string, string>>({});
  const [sortBy, setSortBy] = useState('name');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [viewMode, setViewMode] = useState<ViewMode>('grid');
  const [selectedFranchiseId, setSelectedFranchiseId] = useState<string>('all');

  // Function to get the franchiseId for creating new branches
  const getNewBranchFranchiseId = (): string | null => {
    // For super_admin, use the selected franchise from the dropdown
    if (customClaims?.role === 'super_admin') {
      return selectedFranchiseId !== 'all' ? selectedFranchiseId : null;
    }
    // For franchise_owner or admin, use their franchiseId
    return user?.franchiseId || null;
  };

  const handleNewBranch = () => {
    const franchiseId = getNewBranchFranchiseId();

    if (!franchiseId) {
      toast.error('Por favor selecciona una franquicia primero');
      return;
    }

    navigate(`/branches/new?franchiseId=${franchiseId}`);
  };

  useEffect(() => {
    refreshBranches();
  }, []);

  const filterConfigs: FilterConfig[] = [
    {
      key: 'isActive',
      label: t('branches.filters.status'),
      options: [
        { value: 'true', label: t('common.active') },
        { value: 'false', label: t('common.inactive') },
      ],
    },
  ];

  const sortOptions: SortOption[] = [
    { value: 'name', label: t('branches.sort.name') },
    { value: 'city', label: t('branches.sort.city') },
    { value: 'address', label: t('branches.sort.address') },
    { value: 'createdAt', label: t('branches.sort.createdAt') },
  ];

  const filteredBranches = useMemo(() => {
    let result = branches || [];

    // Franchise filter (only for super_admin)
    if (customClaims?.role === 'super_admin' && selectedFranchiseId && selectedFranchiseId !== 'all') {
      result = result.filter(b => b.franchiseId === selectedFranchiseId);
    }

    // Search filter
    if (searchQuery) {
      result = result.filter(b =>
        b.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        b.city.toLowerCase().includes(searchQuery.toLowerCase()) ||
        b.address.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    // Active filters
    if (activeFilters.isActive) {
      result = result.filter(b => b.isActive === (activeFilters.isActive === 'true'));
    }

    // Sorting
    result.sort((a, b) => {
      let aValue: any = a[sortBy as keyof typeof a];
      let bValue: any = b[sortBy as keyof typeof b];

      // Handle Firestore Timestamps
      if (sortBy === 'createdAt' && aValue?.toDate && bValue?.toDate) {
        aValue = aValue.toDate().getTime();
        bValue = bValue.toDate().getTime();
      }

      // Handle string comparison
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    return result;
  }, [branches, searchQuery, activeFilters, sortBy, sortDirection, selectedFranchiseId, customClaims]);

  // Pagination
  const totalPages = Math.ceil((filteredBranches?.length || 0) / ITEMS_PER_PAGE);
  const paginatedBranches = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    const end = start + ITEMS_PER_PAGE;
    return filteredBranches?.slice(start, end);
  }, [filteredBranches, currentPage]);

  const handleDelete = async () => {
    if (!deleteId) return;
    await deleteBranch(deleteId);
    setDeleteId(null);
  };

  const handleFilterChange = (filters: Record<string, string>) => {
    setActiveFilters(filters);
    setCurrentPage(1);
  };

  const handleSearchChange = (query: string) => {
    setSearchQuery(query);
    setCurrentPage(1);
  };

  if (loading) {
    return <LoadingState message={t('branches.loading.branches')} variant="skeleton" />;
  }

  return (
    <div className="container mx-auto p-6">
      {/* Breadcrumbs */}
      <Breadcrumbs items={[{ label: t('branches.title') }]} />

      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-ink">{t('branches.title')}</h1>
          <p className="text-ink-muted mt-1">
            {t('branches.count.total', {
              count: filteredBranches?.length || 0,
              defaultValue: `${filteredBranches?.length || 0} total ${filteredBranches?.length === 1 ? 'branch' : 'branches'}`
            })}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <ExportButton data={filteredBranches || []} filename="branches" />
          <ViewToggle view={viewMode} onChange={setViewMode} />
          <Button onClick={handleNewBranch}>
            + {t('branches.new')}
          </Button>
        </div>
      </div>

      {/* Franchise Selector (only for super_admin) */}
      {customClaims?.role === 'super_admin' && franchises && franchises.length > 0 && (
        <div className="mb-4">
          <div className="flex items-center gap-3 bg-surface p-4 rounded-lg border border-hairline">
            <label htmlFor="franchise-select" className="text-sm font-medium text-ink-muted whitespace-nowrap">
              🏢 Franquicia:
            </label>
            <select
              id="franchise-select"
              value={selectedFranchiseId}
              onChange={(e) => {
                setSelectedFranchiseId(e.target.value);
                setCurrentPage(1);
              }}
              className="flex-1 px-4 py-2 border border-hairline rounded-lg focus:ring-2 focus:ring-focus focus:border-transparent bg-surface text-ink"
            >
              <option value="all">Todas las franquicias</option>
              {franchises.filter(f => f.isActive).map(franchise => (
                <option key={franchise.franchiseId} value={franchise.franchiseId}>
                  {franchise.name}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}

      {/* Search Bar */}
      <div className="mb-4">
        <SearchBar
          value={searchQuery}
          onChange={handleSearchChange}
          placeholder={t('branches.search.placeholder')}
        />
      </div>

      {/* Filter Bar and Sort */}
      <div className="mb-6 flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
        <FilterBar
          filters={filterConfigs}
          onFilterChange={handleFilterChange}
          onReset={() => setSearchQuery('')}
        />
        <SortSelector
          options={sortOptions}
          value={sortBy}
          direction={sortDirection}
          onChange={setSortBy}
          onDirectionChange={setSortDirection}
        />
      </div>

      {/* Content */}
      {!paginatedBranches || paginatedBranches.length === 0 ? (
        <EmptyState
          icon="📍"
          title={t('branches.empty.title')}
          message={searchQuery || Object.keys(activeFilters).length > 0
            ? t('branches.empty.noResults')
            : t('branches.empty.noFranchises')}
          actionLabel={t('branches.empty.action')}
          onAction={handleNewBranch}
        />
      ) : (
        <>
          {/* Grid or List View */}
          {viewMode === 'grid' ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
              {paginatedBranches.map((branch) => (
                <BranchCard
                  key={branch.branchId}
                  branch={branch}
                  onEdit={(id) => navigate(`/branches/${id}/edit`)}
                  onDelete={setDeleteId}
                />
              ))}
            </div>
          ) : (
            <div className="mb-6">
              <BranchListView
                branches={paginatedBranches}
                onEdit={(id) => navigate(`/branches/${id}/edit`)}
                onDelete={setDeleteId}
              />
            </div>
          )}

          {/* Pagination */}
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
            totalItems={filteredBranches?.length || 0}
            itemsPerPage={ITEMS_PER_PAGE}
          />
        </>
      )}

      <ConfirmDialog
        open={!!deleteId}
        title={t('branches.confirmDelete.title')}
        message={t('branches.confirmDelete.message')}
        variant="danger"
        confirmLabel={t('branches.confirmDelete.confirm')}
        onConfirm={handleDelete}
        onClose={() => setDeleteId(null)}
      />
    </div>
  );
}
